import type { TrackingPageOrder } from "@redotech/order-tracking-common/tracking-page-order-definition";
import { MarketingChannel } from "@redotech/redo-billing-common/marketing-channel";
import type { IanaTimeZoneName } from "@redotech/redo-common-model/timezone-list";
import type { TrackingPageScheduledEvent } from "@redotech/redo-flows-common/tracking-page-scheduled-event";
import { scheduledEventsOverlap } from "@redotech/redo-flows-common/tracking-page-scheduled-event";
import { useTranslation } from "@redotech/redo-i18n";
import ArrowLeftIcon from "@redotech/redo-icons/arrow-left.svg";
import CalendarDateIcon from "@redotech/redo-icons/calendar-date.svg";
import ClockIcon from "@redotech/redo-icons/clock.svg";
import {
  filterTimezoneOptions,
  formatTimeOfDay,
  getTimePresets,
  TIMEZONE_OPTIONS,
} from "@redotech/redo-merchant-marketing-app/campaigns/edit/components/schedule-campaign-drawer-utils";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoSingleDatePicker } from "@redotech/redo-web/arbiter-components/date-picker/redo-single-date-picker";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import type { TimeOfDay } from "@redotech/redo-web/arbiter-components/time/redo-time-picker";
import { RedoTimePicker } from "@redotech/redo-web/arbiter-components/time/redo-time-picker";
import {
  dateToPlainDate,
  dateToTimeOfDay,
  plainDateToDate,
} from "@redotech/redo-web/arbiter-components/time/time-zone-conversion";
import { Flex } from "@redotech/redo-web/flex";
import { Popover } from "@redotech/redo-web/popover/popover";
import { Text } from "@redotech/redo-web/text";
import { ObjectId } from "bson";
import { useState } from "react";
import type { PartialScheduledEvent } from "./scheduled-events-section";
import type { TrackingPageOption } from "./tracking-page-picker";
import { TrackingPagePicker } from "./tracking-page-picker";

const CUSTOM_OPTION_ID = "custom";

function TimeOfDayField({
  date,
  timeZone,
  value,
  setValue,
}: {
  date: Temporal.PlainDate | undefined;
  timeZone: IanaTimeZoneName;
  value: TimeOfDay | null;
  setValue: (value: TimeOfDay | null) => void;
}) {
  const { t } = useTranslation();
  const presets = getTimePresets(
    date ?? Temporal.Now.plainDateISO(timeZone),
    MarketingChannel.EMAIL,
    timeZone,
  );

  const presetItem = value
    ? presets.find(
        (option) =>
          option.value.hours === value.hours &&
          option.value.minutes === value.minutes,
      )
    : undefined;
  const isCustom = !!value && !presetItem;

  const customOption = {
    id: CUSTOM_OPTION_ID,
    type: "text" as const,
    text: t("Custom time"),
    value: { hours: -1, minutes: -1 } as TimeOfDay,
  };

  const allOptions = [customOption, ...presets];

  const selectedItem = isCustom
    ? {
        ...customOption,
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        text: formatTimeOfDay(value!),
        leadingItem: { type: "icon" as const, Icon: ClockIcon },
      }
    : presetItem
      ? {
          ...presetItem,
          leadingItem: { type: "icon" as const, Icon: ClockIcon },
        }
      : undefined;

  const [popoverOpen, setPopoverOpen] = useState(false);
  const [popoverAnchor, setPopoverAnchor] = useState<HTMLDivElement | null>(
    null,
  );
  const [pendingTime, setPendingTime] = useState<TimeOfDay | null>(null);

  return (
    <>
      <Flex grow={1} minW="none" ref={setPopoverAnchor}>
        <RedoSingleSelectDropdownInput<TimeOfDay>
          fitToAnchor={false}
          options={allOptions}
          optionSelected={({ item }) => {
            if (item.id === CUSTOM_OPTION_ID) {
              setPendingTime(isCustom ? value : null);
              setPopoverOpen(true);
            } else {
              setValue(item.value);
            }
          }}
          placeholder={t("Select time")}
          selectedItem={selectedItem}
          size="xs"
        />
      </Flex>
      <Popover
        anchorEl={popoverAnchor}
        onClose={() => setPopoverOpen(false)}
        open={popoverOpen}
      >
        <Flex dir="column" gap="sm">
          <Text fontSize="xs" textColor="secondary">
            {t("Enter a custom time")}
          </Text>
          <RedoTimePicker
            clearIcon={null}
            setValue={setPendingTime}
            value={pendingTime}
          />
          <RedoButton
            disabled={!pendingTime}
            hierarchy="primary"
            onClick={() => {
              if (pendingTime) {
                setValue(pendingTime);
              }
              setPopoverOpen(false);
            }}
            size="sm"
            text={t("Save")}
          />
        </Flex>
      </Popover>
    </>
  );
}

export function ScheduledEventEditor({
  event,
  otherEvents,
  trackingPages,
  stepId,
  timezone,
  previewOrder,
  onSave,
  onBack,
  onPageCreated,
}: {
  event: PartialScheduledEvent | undefined;
  /** The step's other events (excluding `event`), for overlap validation. */
  otherEvents: TrackingPageScheduledEvent[];
  trackingPages: TrackingPageOption[] | undefined;
  stepId: string;
  /** Real order for the thumbnail's data-bound blocks; mock when absent. */
  previewOrder?: TrackingPageOrder;
  /** The viewer's IANA timezone; windows are displayed and authored in it. */
  timezone: IanaTimeZoneName;
  onSave: (event: TrackingPageScheduledEvent) => void;
  onBack: () => void;
  onPageCreated?: (page: TrackingPageOption) => void;
}) {
  const { t } = useTranslation();
  const isEditing = !!event;
  const eventIsLive =
    !!event?.startAt &&
    new Date(event.startAt).getTime() < Date.now() &&
    !!event.endAt &&
    new Date(event.endAt).getTime() > Date.now();

  const [eventTimezone, setEventTimezone] = useState<IanaTimeZoneName>(
    event?.timeZone ?? timezone,
  );
  const [timezoneSearch, setTimezoneSearch] = useState("");

  const [name, setName] = useState(event?.name ?? "");
  const [startDate, setStartDate] = useState<Temporal.PlainDate | undefined>(
    event?.startAt ? dateToPlainDate(event.startAt, eventTimezone) : undefined,
  );
  const [startTime, setStartTime] = useState<TimeOfDay | null>(
    event?.startAt ? dateToTimeOfDay(event.startAt, eventTimezone) : null,
  );
  const [endDate, setEndDate] = useState<Temporal.PlainDate | undefined>(
    event?.endAt ? dateToPlainDate(event.endAt, eventTimezone) : undefined,
  );
  const [endTime, setEndTime] = useState<TimeOfDay | null>(
    event?.endAt ? dateToTimeOfDay(event.endAt, eventTimezone) : null,
  );
  const [trackingPageId, setTrackingPageId] = useState<string | undefined>(
    event?.trackingPageId,
  );

  const draftStartAt =
    startDate && startTime
      ? plainDateToDate(startDate, startTime, eventTimezone)
      : undefined;
  const draftEndAt =
    endDate && endTime
      ? plainDateToDate(endDate, endTime, eventTimezone)
      : undefined;
  const draftWindow =
    draftStartAt && draftEndAt
      ? { startAt: draftStartAt, endAt: draftEndAt }
      : undefined;
  const endBeforeStart =
    !!draftStartAt && !!draftEndAt && draftEndAt <= draftStartAt;
  const minuteMs = (date: Date) => Math.floor(date.getTime() / 60_000);
  const originalStartMinute = event?.startAt
    ? minuteMs(new Date(event.startAt))
    : undefined;
  const startInPast =
    !!draftStartAt &&
    minuteMs(draftStartAt) !== originalStartMinute &&
    draftStartAt.getTime() < Date.now();
  const originalEndMinute = event?.endAt
    ? minuteMs(new Date(event.endAt))
    : undefined;
  const endInPast =
    !!draftEndAt &&
    minuteMs(draftEndAt) !== originalEndMinute &&
    draftEndAt.getTime() < Date.now();
  // Check the draft against each other event individually so a pre-existing
  // overlap between two other events can't block saving this one.
  const overlapping =
    !!draftWindow &&
    !endBeforeStart &&
    otherEvents.some((other) => scheduledEventsOverlap([other, draftWindow]));

  const todayInZone = Temporal.Now.plainDateISO(eventTimezone);
  const endMinDate =
    startDate && Temporal.PlainDate.compare(startDate, todayInZone) > 0
      ? startDate
      : todayInZone;

  const canSave =
    !!name.trim() &&
    !!draftWindow &&
    !!trackingPageId &&
    !endBeforeStart &&
    !startInPast &&
    !endInPast &&
    !overlapping;

  const save = () => {
    if (!canSave || !draftWindow || !trackingPageId) {
      return;
    }
    onSave({
      id: event?.id ?? new ObjectId().toString(),
      name: name.trim(),
      trackingPageId,
      startAt: draftWindow.startAt,
      endAt: draftWindow.endAt,
      timeZone: eventTimezone,
    });
  };

  return (
    <Flex dir="column" gap="2xl" pb="3xl">
      <Flex dir="column" gap="lg">
        <Flex>
          <RedoButton
            hierarchy="link"
            IconLeading={ArrowLeftIcon}
            onClick={onBack}
            size="sm"
            text={t("Back")}
          />
        </Flex>
        <Flex align="center" dir="row" gap="md">
          <CalendarDateIcon height="20" width="20" />
          <Text fontSize="sm" fontWeight="semibold">
            {isEditing
              ? t("Edit scheduled event")
              : t("Schedule tracking page")}
          </Text>
        </Flex>
      </Flex>

      <Flex dir="column" gap="lg">
        <Flex dir="column" gap="xxs">
          <Text fontSize="xs" textColor="secondary">
            {t("Name")}
          </Text>
          <RedoTextInput
            placeholder={t("Add a name")}
            setValue={setName}
            size="sm"
            value={name}
          />
        </Flex>

        <Flex dir="column" gap="xxs">
          <Text fontSize="xs" textColor="secondary">
            {t("Start")}
          </Text>
          <Flex dir="row" gap="xs">
            <Flex grow={1} minW="none">
              <RedoSingleDatePicker
                buttonSize="sm"
                date={startDate}
                minDate={todayInZone}
                setDate={setStartDate}
              />
            </Flex>
            <TimeOfDayField
              date={startDate}
              setValue={setStartTime}
              timeZone={eventTimezone}
              value={startTime}
            />
          </Flex>
          {startInPast && (
            <Text fontSize="xs" textColor="error">
              {t("Please select a future time.")}
            </Text>
          )}
        </Flex>

        <Flex dir="column" gap="xxs">
          <Text fontSize="xs" textColor="secondary">
            {t("End")}
          </Text>
          <Flex dir="row" gap="xs">
            <Flex grow={1} minW="none">
              <RedoSingleDatePicker
                buttonSize="sm"
                date={endDate}
                minDate={endMinDate}
                setDate={setEndDate}
              />
            </Flex>
            <TimeOfDayField
              date={endDate}
              setValue={setEndTime}
              timeZone={eventTimezone}
              value={endTime}
            />
          </Flex>
          {endBeforeStart ? (
            <Text fontSize="xs" textColor="error">
              {t("End must be after start.")}
            </Text>
          ) : (
            endInPast && (
              <Text fontSize="xs" textColor="error">
                {t("Please select a future time.")}
              </Text>
            )
          )}
        </Flex>

        <Flex dir="column" gap="xxs">
          <Text fontSize="xs" textColor="secondary">
            {t("Time zone")}
          </Text>
          <RedoSingleSelectDropdownInput<IanaTimeZoneName>
            disabled={eventIsLive}
            options={filterTimezoneOptions(TIMEZONE_OPTIONS, timezoneSearch)}
            optionSelected={({ item }) => setEventTimezone(item.value)}
            placeholder={t("Select timezone")}
            searchPlaceholder={t("Search timezones...")}
            searchString={timezoneSearch}
            selectedItem={TIMEZONE_OPTIONS.find(
              (item) => item.value === eventTimezone,
            )}
            setSearchString={setTimezoneSearch}
            size="xs"
          />
          {eventIsLive && (
            <Text fontSize="xs" textColor="secondary">
              {t("The time zone can't be changed while the event is live.")}
            </Text>
          )}
        </Flex>

        {overlapping && (
          <Text fontSize="xs" textColor="error">
            {t(
              "This window overlaps another scheduled event. Adjust the dates so only one event is live at a time.",
            )}
          </Text>
        )}
      </Flex>

      <TrackingPagePicker
        label={t("Tracking page")}
        onPageCreated={onPageCreated}
        previewOrder={previewOrder}
        setTrackingPageId={setTrackingPageId}
        stepId={stepId}
        trackingPageId={trackingPageId}
        trackingPages={trackingPages}
      />

      <Flex dir="column" gap="sm">
        <RedoButton
          disabled={!canSave}
          hierarchy="primary"
          onClick={save}
          size="md"
          text={isEditing ? t("Save event") : t("Create event")}
        />
        <Text fontSize="xs" textAlign="center" textColor="secondary">
          {t(
            "While an event is live, it replaces the step's default tracking page. Events cannot overlap.",
          )}
        </Text>
      </Flex>
    </Flex>
  );
}
